import type { LearnTopic } from "../topic-types";

/**
 * Emotional Flashbacks.
 *
 * Add new guides, exercises, prompts or worksheets by appending to a
 * section's `resources` array, or add a new section entirely.
 */
export const EMOTIONAL_FLASHBACKS_TOPIC: LearnTopic = {
  slug: "emotional-flashbacks",
  emoji: "⚡",
  title: "Emotional Flashbacks",
  tagline: "When the past arrives as a feeling",
  theme: "lavender",
  seoDescription:
    "Understand emotional flashbacks — sudden waves of intense feeling connected to past experience, without a clear visual memory. Learn to recognise, ground through, and plan for them. Trauma-informed education from Recovery Trauma™.",
  category: "cptsd-responses",
  cardBlurb: "Understand sudden emotional waves from the past and build a personal plan.",
  isNew: true,
  intro: [
    "An emotional flashback is a sudden, intense wave of feeling — fear, shame, grief, powerlessness — that seems to come from nowhere. Unlike the flashbacks often shown in films, there may be no image, no clear memory, just a feeling that arrives all at once and can feel disproportionate to what's actually happening.",
    "Many people describe emotional flashbacks as suddenly feeling much younger than their age, or reacting to a present-day situation as though it carries the full weight of something from the past. This can be confusing and often isolating, especially without a name for what's happening.",
    "This guide is educational, not a diagnostic tool. The term 'emotional flashback' is widely used in trauma-informed writing, though it is not a formal clinical diagnosis in itself. If these experiences feel frequent or overwhelming, support from a trauma-informed therapist can make a real difference alongside anything you explore here.",
  ],
  closing:
    "Emotional flashbacks can feel disorientating, but they are a recognised and understandable response to past experience — not a sign that something is wrong with you. Having a plan in place, even a simple one, can make these moments feel a little more manageable. If flashbacks are frequent, intense, or affecting your daily life, please consider reaching out to a GP or trauma-informed therapist, and remember that crisis support is always available if things feel too much.",
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What emotional flashbacks are, and how they differ from other kinds of flashback.",
      resources: [
        {
          slug: "what-is-an-emotional-flashback",
          kind: "learn",
          title: "What is an emotional flashback?",
          summary: "A wave of intense feeling connected to the past, without a clear image.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "An emotional flashback is when a feeling from a past experience — often from childhood or an earlier difficult period — resurfaces intensely in the present, triggered by something that resembles it in some way. The trigger can be small: a tone of voice, a particular silence, a sense of being criticised or dismissed.",
            },
            {
              type: "prose",
              text: "The term was popularised by psychologist Pete Walker in his work on complex trauma, describing flashbacks that carry feeling but not necessarily a visual memory. It has since become a widely used, informal way of describing this experience — though it is not itself a diagnosis.",
            },
            {
              type: "list",
              title: "Some key things to know",
              items: [
                "Emotional flashbacks can happen without any conscious memory of the original event",
                "They can feel completely disproportionate to what's happening right now",
                "They are often accompanied by a strong urge to fight, flee, freeze or fawn",
                "They tend to fade, even though in the moment they can feel endless",
              ],
            },
            {
              type: "note",
              text: "This page describes a widely recognised pattern in trauma-informed writing, not a formal diagnostic category. A mental health professional can help assess what's happening for you individually.",
            },
          ],
        },
        {
          slug: "emotional-vs-visual-flashbacks",
          kind: "learn",
          title: "Emotional vs visual flashbacks",
          summary: "How feeling-based flashbacks differ from the flashbacks shown in films.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "When most people imagine a 'flashback', they picture a vivid, film-like memory replaying — sounds, images, sometimes full scenes. This is often called a visual or sensory flashback, and it's commonly associated with PTSD.",
            },
            {
              type: "prose",
              text: "Emotional flashbacks are different. There may be no image at all — just an overwhelming feeling that seems to appear from nowhere, often with body sensations attached (a tight chest, nausea, an urge to run or hide). Because there's no clear memory attached, it can be much harder to recognise what's actually happening.",
            },
            {
              type: "list",
              title: "This can make emotional flashbacks",
              items: [
                "Harder to identify in the moment, because there's no obvious 'replay'",
                "Easy to mistake for an overreaction to the present situation",
                "Confusing to explain to other people, or even to yourself",
                "Sometimes accompanied by a felt sense of an age much younger than you are now",
              ],
            },
          ],
        },
        {
          slug: "common-signs",
          kind: "learn",
          title: "Common signs of an emotional flashback",
          summary: "What people often notice during and after.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "During",
              items: [
                "A sudden, intense feeling — fear, shame, panic, grief, powerlessness",
                "A sense of shrinking, or feeling much younger than your actual age",
                "An urge to fight, flee, freeze or appease someone",
                "Feeling small, unseen, or as though you have no options",
                "A physical reaction: racing heart, shaking, nausea, a tight throat",
              ],
            },
            {
              type: "list",
              title: "Afterwards",
              items: [
                "Exhaustion, as though you've been through something physically demanding",
                "Shame or embarrassment about how you reacted",
                "Confusion about why the feeling was so strong",
                "A slow, gradual return to feeling more like 'yourself'",
              ],
            },
            {
              type: "note",
              text: "None of these signs mean something is wrong with you. They are signals that your nervous system linked the present moment to something from the past.",
            },
          ],
        },
        {
          slug: "why-younger-frightened-ashamed",
          kind: "learn",
          title: "Why you might suddenly feel younger, frightened or ashamed",
          summary: "Understanding the felt sense of being back in an earlier moment.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "During an emotional flashback, some people describe feeling as though they've been transported back to being a child — small, frightened, powerless, or deeply ashamed — even though nothing about the present situation is objectively dangerous.",
            },
            {
              type: "prose",
              text: "One way of understanding this: if a feeling was too overwhelming to process fully at the time it first happened (particularly in childhood, when we have fewer resources to cope), it can remain somewhat 'unfinished'. A present-day trigger that resembles the original situation, even loosely, can reactivate that stored feeling in full force.",
            },
            {
              type: "list",
              title: "This might explain feelings such as",
              items: [
                "A sudden fear of being told off, even by someone with no authority over you",
                "Shame that feels bigger than the situation warrants",
                "A powerlessness that doesn't match your actual ability to leave or respond",
                "An urgent need for someone else's reassurance or approval",
              ],
            },
            {
              type: "note",
              text: "This is an educational framework for understanding a common experience — not a clinical explanation of your individual history. A trauma-informed therapist can help you explore your own patterns more specifically and safely.",
            },
          ],
        },
        {
          slug: "recognising-triggers",
          kind: "learn",
          title: "Recognising your triggers",
          summary: "The kinds of moments that can set off an emotional flashback.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "Common triggers people report",
              items: [
                "Being criticised, corrected or disagreed with",
                "Someone raising their voice, or going silent",
                "Feeling excluded, ignored or unimportant",
                "Being rushed, pressured or unable to leave a situation",
                "A tone of voice or facial expression that resembles someone from the past",
              ],
            },
            {
              type: "prose",
              text: "Triggers are rarely obvious at first. It can take time and gentle attention to notice patterns — often it's easier to spot them looking back than in the moment itself.",
            },
            {
              type: "note",
              text: "Our Triggers guide covers this topic in more depth, including how to track and understand your own triggers over time.",
            },
          ],
        },
      ],
    },
    {
      slug: "in-the-moment",
      emoji: "🫧",
      title: "In the moment",
      blurb: "Grounding techniques to use while a flashback is happening.",
      resources: [
        {
          slug: "grounding-during-a-flashback",
          kind: "practice",
          title: "Grounding during a flashback",
          summary: "A step-by-step grounding practice for emotional flashbacks.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "The aim of grounding during an emotional flashback isn't to stop the feeling immediately — that's rarely possible, and isn't the goal. It's to gently remind your nervous system that you are here, now, as the age you actually are, and that this moment is different from the one being echoed.",
            },
            {
              type: "steps",
              title: "A grounding sequence to try",
              items: [
                { label: "Say to yourself: 'I am having an emotional flashback'", note: "Naming it can create a small but important distance." },
                { label: "Remind yourself of your age and the date", note: "\"I am [your age]. It is [today's date]. I am safe in this moment.\"" },
                { label: "Feel your feet on the ground", note: "Press down gently, notice the support beneath you." },
                { label: "Look around and name what you actually see", note: "The room you're in, right now — not the memory." },
                { label: "Slow your breathing", note: "Try a longer exhale than inhale, a few times." },
                { label: "Offer yourself some reassurance", note: "\"This feeling is real, but it belongs to the past. It will pass.\"" },
              ],
            },
            {
              type: "note",
              text: "You can stop this practice at any point. If it doesn't feel like enough, our full Grounding tool has other techniques, and reaching out to someone you trust is always a valid option.",
            },
          ],
        },
        {
          slug: "what-to-do-afterwards",
          kind: "learn",
          title: "What to do afterwards",
          summary: "Caring for yourself once the intensity has passed.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "The period after an emotional flashback can bring its own challenges — tiredness, shame, or a sense of needing to explain or apologise for how you reacted. Being gentle with yourself in this window can matter as much as what you do during the flashback itself.",
            },
            {
              type: "list",
              title: "Things that can help",
              items: [
                "Resting, even briefly, rather than rushing back into activity",
                "Drinking water and eating something if you're able to",
                "Avoiding major decisions or difficult conversations for a little while",
                "Writing down what happened, once you feel able, to look back on later",
                "Reminding yourself that the intensity was real, but it has passed",
              ],
            },
            {
              type: "note",
              text: "There's no need to apologise for having an emotional flashback. If it happened around someone you trust, it may (when you're ready) be worth letting them know what helps you, so they can support you next time.",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise",
      blurb: "Building a personal plan for spotting and navigating flashbacks.",
      resources: [
        {
          slug: "flashback-plan-worksheet",
          kind: "worksheet",
          title: "My Emotional Flashback Plan",
          summary: "A printable worksheet to prepare for emotional flashbacks before they happen.",
          minutes: 12,
          blocks: [
            {
              type: "prose",
              text: "Having a plan written down in advance can make emotional flashbacks feel more manageable, because you don't have to think clearly in the moment — you can simply follow what you've already prepared for yourself. Fill in what feels true for you; you can update this over time as you learn more about your own patterns.",
            },
            {
              type: "note",
              text: "This worksheet is a self-support tool, not a substitute for professional care. If flashbacks are frequent or distressing, please consider sharing this plan with a therapist or GP.",
            },
          ],
          worksheetFields: [
            { label: "Early signs I notice", hint: "Physical sensations, thoughts or feelings that show up first", lines: 3 },
            { label: "My common triggers", hint: "Situations, tones of voice, types of comments, or environments", lines: 3 },
            { label: "What helps in the moment", hint: "Grounding techniques, phrases, movements, or objects that help", lines: 3 },
            { label: "People I can contact", hint: "Names and how you'll reach them, if you want support", lines: 3 },
            { label: "What I need afterwards", hint: "Rest, quiet, food, water, distraction, or something else", lines: 3 },
          ],
        },
        {
          slug: "practising-the-plan",
          kind: "practice",
          title: "Practising your plan before you need it",
          summary: "Rehearsing your flashback plan in a calm moment.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "It can help to read through your flashback plan when you're feeling calm, rather than only during a difficult moment. This gives your nervous system a chance to become familiar with the steps in advance.",
            },
            {
              type: "steps",
              title: "A short rehearsal",
              items: [
                { label: "Read your plan slowly, once, in a calm moment", note: "Notice if anything feels missing or needs adjusting." },
                { label: "Picture a recent smaller trigger", note: "Imagine gently applying one step from your plan." },
                { label: "Keep your plan somewhere easy to find", note: "Your phone notes, journal, or somewhere visible at home." },
                { label: "Revisit and update it over time", note: "Your triggers and what helps may shift as you learn more." },
              ],
            },
            {
              type: "note",
              text: "You can stop this exercise at any point, especially if imagining a trigger feels too activating right now.",
            },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Journal prompts to explore emotional flashbacks with curiosity and care.",
      resources: [
        {
          slug: "reflecting-on-flashbacks",
          kind: "reflect",
          title: "Reflecting on my emotional flashbacks",
          summary: "Prompts for exploring patterns gently, in your own time.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              items: [
                "Thinking back to a recent emotional flashback, what age did I feel in that moment?",
                "What might that younger part of me have needed to hear at the time?",
                "What does my body tend to do first when a flashback begins?",
                "Is there a phrase or reminder that helps bring me back to the present?",
                "What is one small piece of my flashback plan I'd like to try this week?",
                "How has my understanding of this experience changed since I started learning about it?",
              ],
            },
            {
              type: "note",
              text: "You can save these reflections in your Journal and revisit them over time to notice how your patterns and self-understanding shift.",
            },
          ],
        },
      ],
    },
  ],
  continueTo: [
    { label: "Grounding", to: "/grounding" },
    { label: "C-PTSD", to: "/learn/cptsd" },
    { label: "Window of Tolerance", to: "/learn/window-of-tolerance" },
    { label: "Journal", to: "/journal" },
    { label: "Crisis Resources", to: "/crisis" },
  ],
};
